/**
 * Adventure Mode Type Definitions
 * アドベンチャーモードの型定義
 *
 * Created: 2025-12-17
 */

// ============================================================
// NPC関連の型
// ============================================================

/**
 * NPC ID型（competitors.tsのキーと一致）
 */
export type NpcId =
  | 'elda'
  | 'sisterEmma'
  | 'seira'
  | 'misty'
  | 'pippo'
  | 'rowen'
  | 'garon'
  | 'toma'
  | 'malik'
  | 'liliane'
  | 'nova'
  | 'profNome'
  | 'galdos'
  | 'kain'
  | 'mask'
  | 'doppelganger';

/**
 * 難易度ランク
 */
export type DifficultyTier = 'S' | 'A' | 'B' | 'C' | 'D';

/**
 * 難易度星表示（1-5）
 */
export type DifficultyStars = 1 | 2 | 3 | 4 | 5;

/**
 * アドベンチャー用NPC拡張情報
 */
export interface AdventureNpcConfig {
  npcId: NpcId;
  chapter: number;                    // 所属章（1-6）
  order: number;                      // 章内の順番
  deckName: string;                   // 使用デッキ名
  winRate: number;                    // シミュレーション勝率（参考値）
  tier: DifficultyTier;               // 難易度ティア
  difficultyStars: DifficultyStars;   // 難易度星（1-5）
  unlockRequirements: NpcId[];        // 解放に必要なNPC（AND条件）
  firstClearReward: Reward;           // 初回クリア報酬
  repeatReward: RepeatReward;         // 再戦報酬
}

// ============================================================
// 章関連の型
// ============================================================

/**
 * 章の設定
 */
export interface ChapterConfig {
  id: number;                         // 章番号（1-6）
  name: string;                       // 章名
  description: string;                // 章の説明
  npcs: NpcId[];                      // 所属NPC（順番通り）
  unlockRequirement: NpcId | null;    // 章解放に必要なNPC（前章ラストNPC）
}

// ============================================================
// 報酬関連の型
// ============================================================

/**
 * パック報酬
 */
export interface PackReward {
  type: 'normal' | 'fire' | 'water' | 'light' | 'dark' | 'primitive' | 'future' | 'royal';
  count: number;
}

/**
 * 初回クリア報酬
 */
export interface Reward {
  gold: number;
  packs: PackReward[];
  title?: string;                     // 称号（特別報酬）
}

/**
 * 再戦報酬
 */
export interface RepeatReward {
  goldMin: number;
  goldMax: number;
  packChance: number;                 // パックドロップ確率（0-1）
  packType?: PackReward['type'];      // ドロップするパックの種類
}

// ============================================================
// 進行データの型
// ============================================================

/**
 * 各NPCとの対戦記録
 */
export interface NpcBattleRecord {
  wins: number;                       // 勝利数
  losses: number;                     // 敗北数
  firstClearDate: string | null;      // 初回クリア日（ISO日付）
  lastBattleDate: string | null;      // 最終対戦日（ISO日付）
  todayBattleCount: number;           // 本日の対戦回数
  todayRewardCount: number;           // 本日の報酬獲得回数
}

/**
 * アドベンチャー進行データ
 */
export interface AdventureProgress {
  // クリア状況
  clearedNpcs: NpcId[];               // クリア済みNPC ID配列

  // 各NPCとの戦績
  npcRecords: Partial<Record<NpcId, NpcBattleRecord>>;

  // 統計
  totalBattles: number;
  totalWins: number;
  totalLosses: number;

  // 称号
  titles: string[];

  // メタデータ
  version: number;                    // データバージョン（マイグレーション用）
  lastUpdated: string;                // 最終更新日（ISO日付）
}

// ============================================================
// UI状態の型
// ============================================================

/**
 * NPC解放状態
 */
export type NpcUnlockStatus = 'cleared' | 'unlocked' | 'locked';

/**
 * NPC表示情報（UI用）
 */
export interface NpcDisplayInfo {
  npcId: NpcId;
  name: string;
  portrait: string;
  deckName: string;
  difficultyStars: DifficultyStars;
  tier: DifficultyTier;
  status: NpcUnlockStatus;
  record: NpcBattleRecord | null;
  firstClearReward: Reward;
  unlockRequirements: NpcId[];
}

/**
 * 章表示情報（UI用）
 */
export interface ChapterDisplayInfo {
  id: number;
  name: string;
  description: string;
  isUnlocked: boolean;
  progress: {
    cleared: number;
    total: number;
  };
  npcs: NpcDisplayInfo[];
}

// ============================================================
// 対戦関連の型
// ============================================================

/**
 * 対戦結果
 */
export type BattleResult = 'win' | 'lose';

/**
 * 対戦設定
 */
export interface AdventureBattleConfig {
  npcId: NpcId;
  npcName: string;
  npcDeckName: string;
  npcDeckCardIds: string[];
  aiDifficulty: 'easy' | 'normal' | 'hard';
  isFirstBattle: boolean;             // 初回対戦かどうか
}

/**
 * 対戦報酬結果
 */
export interface BattleRewardResult {
  gold: number;
  packs: PackReward[];
  title?: string;
  newUnlocks: NpcId[];                // 新たに解放されたNPC
  isFirstClear: boolean;
}

// ============================================================
// ストレージ関連の型
// ============================================================

/**
 * ストレージキー
 */
export const ADVENTURE_STORAGE_KEY = 'magicSpirit_adventureProgress';

/**
 * 現在のデータバージョン
 */
export const ADVENTURE_DATA_VERSION = 1;
